import { type CountryCode, getCountries, getCountryCallingCode } from "libphonenumber-js/min";

export { type CountryCode };

/**
 * Countries surfaced at the top of country pickers. All other ISO-3166-alpha-2
 * countries from libphonenumber-js follow in the order returned by
 * `getCountries()`.
 */
export const PRIORITY_COUNTRIES: readonly CountryCode[] = ["US", "CA"];

/**
 * Default phone/postal country for a BCP-47 funnel locale, derived from its region subtag
 * ("fr-CA" → CA, "pt-BR" → BR) so new locales pick up the right default without a mapping
 * edit. Locales without a recognized ISO-3166 region (e.g. "es-419") fall back to US.
 */
export function getDefaultCountryForLocale(locale: string | null | undefined): CountryCode {
  const region = locale?.split("-")[1]?.toUpperCase();
  if (region && (getCountries() as readonly string[]).includes(region)) {
    return region as CountryCode;
  }
  return "US";
}

export type CountryMetadata = {
  readonly id: string;
  readonly label: string;
  readonly country: CountryCode;
  readonly dialCode: string;
  readonly flag: string;
};

/**
 * Converts an ISO-3166-alpha-2 country code into its flag emoji by mapping each
 * letter to its regional-indicator code point. Returns an empty string for
 * inputs that are not two uppercase ASCII letters.
 */
export function toFlagEmoji(country: CountryCode): string {
  if (!/^[A-Z]{2}$/.test(country)) {
    return "";
  }

  const [firstCode, secondCode] = country;
  const offset = 127397;
  return String.fromCodePoint(firstCode.charCodeAt(0) + offset, secondCode.charCodeAt(0) + offset);
}

function createDefaultCountries(): readonly CountryMetadata[] {
  const allCountries = getCountries();
  const prioritySet = new Set(PRIORITY_COUNTRIES);
  const orderedCountries = [
    ...PRIORITY_COUNTRIES.filter((country) => allCountries.includes(country)),
    ...allCountries.filter((country) => !prioritySet.has(country)),
  ];

  return orderedCountries.map((country) => ({
    id: country.toLowerCase(),
    label: country,
    country,
    dialCode: `+${getCountryCallingCode(country)}`,
    flag: toFlagEmoji(country),
  }));
}

/**
 * Canonical country dataset shared by country-picking form fields. Computed
 * once at module load; callers that pass a custom `countries` prop opt out of
 * the precomputed `Map` lookup.
 */
export const DEFAULT_COUNTRIES: readonly CountryMetadata[] = createDefaultCountries();

export const DEFAULT_COUNTRIES_BY_CODE: ReadonlyMap<CountryCode, CountryMetadata> = new Map(
  DEFAULT_COUNTRIES.map((c) => [c.country, c]),
);
